import React, { useEffect, useState } from "react";
import ListView from "../../../utils/Custom/listView";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
import { SearchToggleButton } from "../../../utils/Custom/Button";
import { AutoCompletes, DateInput } from "../../../utils/Custom/custominput";
import ExcelDownload from "../../../utils/commonComponent/ExcelDownload";
import { getFormattedDates } from "../../../utils/utils";
import { getPermissions } from "../rbac/hasAccess";
import { statesList } from "../../constants/stateList";

const headers = [ "id", "Client", "address", "poNumber", "jobNumber", "inspectionStartDate", "inspectionNumber", "Items", "inspectorName", "travelFrom", "travelTo", "distance(KM)", "Approx(KM)", "expenditure"];

const notNeededColumn = ["id", "assigneuserID", "empState", "empLocation", "jobState", "jobLocation"];

// Round Trip Distance (KM) -> Fixed Conveyance Amount / Approx Rate per KM
const conveyanceSlabs = [
  { min: 0, max: 20, fixed: 125, rate: 6.25 },
  { min: 21, max: 40, fixed: 255, rate: 6.38 },
  { min: 41, max: 60, fixed: 380, rate: 6.33 },
  { min: 61, max: 80, fixed: 500, rate: 6.25 },
  { min: 81, max: 100, fixed: 615, rate: 6.15 },
  { min: 101, max: 120, fixed: 725, rate: 6.04 },
  { min: 121, max: 140, fixed: 830, rate: 5.93 },
  { min: 141, max: 160, fixed: 930, rate: 5.81 },
  { min: 161, max: 180, fixed: 1025, rate: 5.69 },
  { min: 181, max: 200, fixed: 1115, rate: 5.58 },
  { min: 201, max: 220, fixed: 1200, rate: 5.45 },
  { min: 221, max: 240, fixed: 1280, rate: 5.33 },
  { min: 241, max: 260, fixed: 1355, rate: 5.21 },
  { min: 261, max: 280, fixed: 1425, rate: 5.09 },
  { min: 281, max: 300, fixed: 1490, rate: 4.97 },
];

// distance falls in a slab -> Expenditure = distance * that slab's approx rate/km
const getSlabForDistance = (distance) => {
  if (distance == null || isNaN(distance)) return null;
  return (
    conveyanceSlabs.find((s) => distance >= s.min && distance <= s.max) ||
    conveyanceSlabs[conveyanceSlabs.length - 1]
  );
};

const getExpenditureForDistance = (distance) => {
  if (distance == null) return 0;
  const slab = getSlabForDistance(distance);
  if (!slab) return 0;
  if (distance === 0) return slab.fixed;
  return Number((distance * slab.rate).toFixed(2));
};

const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const toRad = (value) => (value * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// same state/district matching approach used in InspectionTaskList's fetchClientsbyid
const getDistanceBetween = (fromState, fromLocation, toState, toLocation) => {
  if (!fromState || !fromLocation || !toState || !toLocation) return null;

  const matchedFromState = statesList.find((s) => s.label === fromState.trim());
  const matchedFromDistrict = matchedFromState?.district?.find(
    (d) => d.city === fromLocation.trim()
  );

  const matchedToState = statesList.find((s) => s.label === toState.trim());
  const matchedToDistrict = matchedToState?.district?.find(
    (d) => d.city === toLocation.trim()
  );

  if (!matchedFromDistrict || !matchedToDistrict) return null;

  return haversineDistance(
    parseFloat(matchedFromDistrict.lat),
    parseFloat(matchedFromDistrict.lng),
    parseFloat(matchedToDistrict.lat),
    parseFloat(matchedToDistrict.lng)
  );
};

const InspectorExpenseDataReport = (props) => {
  const permissions = getPermissions(props.moduleId);
  const [selectedInspectorId, setSelectedInspectorId] = useState(null);
  const [inspectorList, setInspectorList] = useState([]);
  const [from, setFromDate] = useState(null);
  const [to, setToDate] = useState(null);

  const [ReportList, setReportList] = useState([]);
  const [searched, setSearched] = useState(false);
  const [totalRows, setTotalRows] = useState(0);

  const [currentPage, setCurrentPage] = useState(() => Number(sessionStorage.getItem("ListPageCount") || 0));
  const [rowsPerPage, setRowsPerPage] = useState(() => Number(sessionStorage.getItem("ListRowsPerPage") || 30));

  const handlePaginationChange = (page, rows) => {
    setCurrentPage(page);
    setRowsPerPage(rows);
    sessionStorage.setItem("ListPageCount", page);
    sessionStorage.setItem("ListRowsPerPage", rows);
  };

  const fetchInspectorList = async () => {
    try {
      const query = `SELECT u.id, emp.name FROM users u LEFT JOIN employees emp ON u.employeeId = emp.id WHERE u.status = 1 AND u.type = 2 ORDER BY emp.name ASC`;
      const res = await fetchQuery(query);
      setInspectorList(res || []);
    } catch (error) {
      console.error("Error fetching inspector list:", error);
    }
  };

  const getWhereClause = () => {
    let whereConditions = "";

    if (from != null && to != null) {
      const { fromDate, toDate } = getFormattedDates(from, to);
      whereConditions += whereConditions ? " AND" : " WHERE";
      whereConditions += ` DATE(ir.inspectionDates) BETWEEN '${fromDate}' AND '${toDate}'`;
    }

    if (selectedInspectorId != null) {
      whereConditions += whereConditions ? " AND" : " WHERE";
      whereConditions += ` jri.assigneuserID = ${selectedInspectorId}`;
    }

    return whereConditions;
  };

  const getDataQuery = () => {
    const whereClause = getWhereClause();
    let paginationSettings = "";
    if (rowsPerPage !== -1) {
      paginationSettings = `LIMIT ${rowsPerPage} OFFSET ${rowsPerPage * Number(currentPage)}`;
    }
    return `SELECT
        ir.id AS id,
        jri.assigneuserID,
        c.companyName AS Client,
        addr.address AS address,
        jr.poNumber,
        jr.jobNumber,
        jr.inspectionStartDate,
        jri.inspectionNumber,
        ii.subcategory AS Items,
        emp.name AS inspectorName,
        jr.location AS travelTo,
        jr.state AS jobState,
        jr.location AS jobLocation,
        emp.location AS empLocation,
        emp.state AS empState
        FROM IR ir
        LEFT JOIN jobRegisterInspection jri ON ir.jobRegisterInspectionId = jri.id
        LEFT JOIN jobRegister jr ON jri.jobRegisterId = jr.id
        LEFT JOIN clients c ON jr.clientId = c.id
        LEFT JOIN addresses addr ON jr.address = addr.id
        LEFT JOIN inspectionItems ii ON jri.servicesNameId = ii.id
        LEFT JOIN users u ON jri.assigneuserID = u.id
        LEFT JOIN employees emp ON u.employeeId = emp.id
        ${whereClause}
        ORDER BY ir.id DESC
        ${paginationSettings};`
      .trim()
      .replace(/\s+/g, " ");
  };

  const getCountQuery = () => {
    const whereClause = getWhereClause();
    return `SELECT COUNT(ir.id) AS totalRows
        FROM IR ir
        LEFT JOIN jobRegisterInspection jri ON ir.jobRegisterInspectionId = jri.id
        LEFT JOIN jobRegister jr ON jri.jobRegisterId = jr.id
        LEFT JOIN users u ON jri.assigneuserID = u.id
        LEFT JOIN employees emp ON u.employeeId = emp.id
        ${whereClause};`
      .trim()
      .replace(/\s+/g, " ");
  };

  const fetchReportData = async () => {
    try {
      const [dataResult, totalRowsResult] = await Promise.all([
        fetchQuery(getDataQuery()),
        fetchQuery(getCountQuery()),
      ]);

      const enrichedData = (dataResult || []).map((row) => {
        const distance = getDistanceBetween(
          row.empState,
          row.empLocation,
          row.jobState,
          row.jobLocation
        );
        const roundedDistance = distance != null ? Number(distance.toFixed(2)) : null;
        const slab = getSlabForDistance(roundedDistance);
        return {
          ...row,
          travelFrom:
            row.empLocation && row.empState
              ? `${row.empLocation}, ${row.empState}`
              : row.empLocation || "-",
          travelTo:
            row.jobLocation && row.jobState
              ? `${row.jobLocation}, ${row.jobState}`
              : row.jobLocation || "-",
          "distance(KM)": String(roundedDistance ?? 0),
          "Approx(KM)": slab ? slab.rate : null,
          expenditure: String(getExpenditureForDistance(roundedDistance) ?? 0),
        };
      });

      setReportList(enrichedData);
      setTotalRows(totalRowsResult?.[0]?.totalRows || 0);
    } catch (error) {
      console.error("Error fetching timesheet report:", error);
    }
  };

  const handleSearch = () => {
    setSearched(true);
    setCurrentPage(0);
    fetchReportData();
  };

  const clearSearch = () => {
    setSearched(false);
    setReportList([]);
    setFromDate(null);
    setToDate(null);
    setSelectedInspectorId(null);
  };

  useEffect(() => {
    fetchInspectorList();
  }, []);

  useEffect(() => {
    if (searched) {
      fetchReportData();
    }
  }, [currentPage, rowsPerPage]);

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-2 my-3">
        <div className="w-full">
          <DateInput
            id="from-date"
            label="From Date"
            value={from}
            onChange={(val) => {
              setFromDate(val);
              setSearched(false);
            }}
          />
        </div>
        <div className="w-full">
          <DateInput
            id="to-date"
            label="To Date"
            value={to}
            onChange={(val) => {
              setToDate(val);
              setSearched(false);
            }}
          />
        </div>
        <div className="w-full">
          <AutoCompletes
            label="Inspector"
            options={inspectorList.map((emp) => ({ id: emp.id, label: emp.name }))}
            value={selectedInspectorId}
            onChange={(val) => {
              setSelectedInspectorId(val);
              setSearched(false);
            }}
            isWidth
            valueKey="id"
            labelKey="label"
          />
        </div>
        <div>
          <SearchToggleButton
            searched={searched}
            onSearch={handleSearch}
            onClear={clearSearch}
          />
        </div>
        {ReportList?.length > 0 &&
          (permissions.excelDownload || permissions.allAccess) && (
            <div>
              <ExcelDownload
                finalData={ReportList}
                notNeededColumn={notNeededColumn}
              />
            </div>
          )}
      </div>

      <ListView
        headers={headers}
        data={ReportList}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        totalRows={totalRows}
        onPaginationChange={handlePaginationChange}
      />
    </>
  );
};

export default InspectorExpenseDataReport;